/**
 * Helpers de presentación marketplace compartidos entre cards branded y
 * lot_user_content en el dock. Archivo liviano para evitar ciclos de import.
 */

import { formatSurfaceDisplay, parseSurfaceM2 } from './surfaceDisplay';
import { formatListingPrice, OPERATION_LABEL, getStockStatusLabel, STOCK_STATUSES } from './listingCoreConstants';
import { resolveUnitOperation, resolveUnitStockStatus } from './userLotFormUtils';
import { getOfficialUnitNumber, getOfficialUnitFromUserLot } from './lotFunctionalUnits';
import { getDisplayUnits } from './userLotUnits';
import { normalizeListingImages } from './listingImageUtils';
import { normalizeListingVideos } from './listingVideoUtils';
import { normalizeListingAttachments } from './listingAttachmentUtils';
import {
  formatLotUserContentPrice,
  hasLotUserContentCommercialData,
  LOT_CONTENT_OPERATION_LABEL,
} from './lotUserContentFormUtils';

const firstNonEmpty = (...values) => {
  const found = values.find((value) => {
    if (value === null || value === undefined) return false;
    return !(typeof value === 'string' && value.trim() === '');
  });
  return found === undefined ? null : found;
};

const toDisplayLabel = (value) => {
  const raw = firstNonEmpty(value);
  if (raw == null) return null;
  return String(raw)
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const parseOptionalSurfaceM2 = (value) => {
  const m2 = parseSurfaceM2(value);
  return Number.isNaN(m2) ? null : m2;
};

const formatSurfaceMetaValue = (valueM2) => {
  const display = formatSurfaceDisplay(valueM2);
  return display ? `${display.value} ${display.unit}` : null;
};

/** UF: total cubierta + semicubierta. Lote: superficie asignada (urbansurface). */
const resolveSurfaceLabel = (unit, lot) => {
  if (unit) {
    const covered = parseOptionalSurfaceM2(unit.covered_surface);
    const open = parseOptionalSurfaceM2(unit.open_surface);
    if (covered != null || open != null) {
      return formatSurfaceMetaValue((covered ?? 0) + (open ?? 0));
    }
    return formatSurfaceMetaValue(parseOptionalSurfaceM2(unit.total_surface));
  }

  return formatSurfaceMetaValue(parseOptionalSurfaceM2(firstNonEmpty(
    lot?.urbansurface,
    lot?.lot_urbansurface,
    lot?.surface,
    lot?.lot_surface,
    lot?.filter_attributes?.urbansurface,
    lot?.filter_attributes?.surface,
  )));
};

const hasNumericValue = (value) => {
  if (value === null || value === undefined || value === '') return false;
  const n = Number(value);
  if (Number.isFinite(n)) return n > 0;
  return true;
};

const resolveBalconyValue = (unit) => {
  const attrs = unit?.attributes;
  if (!attrs || typeof attrs !== 'object') return null;
  const raw = attrs.balcony ?? attrs.balcon ?? attrs.has_balcony ?? attrs.has_balcon;
  if (raw === true || raw === 'true' || raw === 1 || raw === '1') return 'Sí';
  if (typeof raw === 'number' && raw > 0) return String(raw);
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  return null;
};

const unitAttr = (unit, key) => {
  if (hasNumericValue(unit?.[key])) return unit[key];
  const attrs = unit?.private_attributes;
  if (attrs && typeof attrs === 'object' && hasNumericValue(attrs[key])) return attrs[key];
  return null;
};

const UNIT_EXTRA_META_DEFS = [
  {
    key: 'bedrooms',
    label: 'Habitaciones',
    shortLabel: 'Hab.',
    resolve: (unit) => {
      const value = unitAttr(unit, 'bedrooms');
      return value == null ? null : String(value);
    },
  },
  {
    key: 'full_bathrooms',
    label: 'Baños',
    resolve: (unit) => {
      const value = unitAttr(unit, 'full_bathrooms');
      return value == null ? null : String(value);
    },
  },
  {
    key: 'parking_lots',
    label: 'Cochera',
    resolve: (unit) => {
      const n = Number(unitAttr(unit, 'parking_lots'));
      if (!Number.isFinite(n) || n <= 0) return null;
      return String(n);
    },
  },
  {
    key: 'open_surface',
    label: 'Semicubierta',
    shortLabel: 'Semicub.',
    resolve: (unit) => formatSurfaceMetaValue(parseOptionalSurfaceM2(unit?.open_surface)),
  },
  {
    key: 'balcony',
    label: 'Balcón',
    resolve: (unit) => resolveBalconyValue(unit),
  },
];

const collectUnitExtraMetas = (unit) => {
  const metas = [];
  UNIT_EXTRA_META_DEFS.forEach((def) => {
    const value = def.resolve(unit);
    if (!value) return;
    metas.push({
      key: def.key,
      label: def.label,
      shortLabel: def.shortLabel || def.label,
      value,
    });
  });
  return metas;
};

/**
 * Metas para card de lote sin UF (contenido del dueño o solo catastro).
 * Superficie / tipo / estado vienen del payload shared (`lot_*`, canónicos o
 * `filter_attributes`); no inventar campos.
 */
export const buildLotContentListingMetas = (lot) => [
  { key: 'surface', label: 'Superficie', shortLabel: 'Sup.', value: resolveSurfaceLabel(null, lot) },
  {
    key: 'type',
    label: 'Tipo',
    value: toDisplayLabel(firstNonEmpty(
      lot?.lot_type,
      lot?.type,
      lot?.filter_attributes?.type,
      lot?.filter_attributes?.lot_type,
    )),
  },
  {
    key: 'state',
    label: 'Estado',
    value: toDisplayLabel(firstNonEmpty(
      lot?.lot_state,
      lot?.state,
      lot?.filter_attributes?.state,
      lot?.filter_attributes?.lot_state,
    )),
  },
].filter((meta) => Boolean(meta.value));

/**
 * Metas para card de UF en vistas compartidas: superficie, estado de stock (paridad CAD)
 * y el resto (habitaciones, baños, cochera, semicubierta, balcón). La card las muestra
 * en una línea; el detalle las lista con label. El estado de la card va en el badge.
 */
export const buildUnitListingMetas = (unit, lot) => {
  if (!unit) return buildLotContentListingMetas(lot);

  const metas = [];
  const officialNumber = getOfficialUnitNumber(getOfficialUnitFromUserLot(unit));
  if (officialNumber) {
    metas.push({
      key: 'official_unit',
      label: 'UF catastral',
      shortLabel: 'UF',
      value: officialNumber,
    });
  }

  const surface = resolveSurfaceLabel(unit, lot);
  if (surface) {
    metas.push({ key: 'surface', label: 'Superficie', shortLabel: 'Sup.', value: surface });
  }

  const stockLabel = getStockStatusLabel(resolveUnitStockStatus(unit));
  if (stockLabel) {
    metas.push({ key: 'stock', label: 'Estado', value: stockLabel });
  }

  metas.push(...collectUnitExtraMetas(unit));
  return metas;
};

const knownStockStatus = (value) => {
  const id = String(value ?? '').trim().toLowerCase();
  return STOCK_STATUSES.includes(id) ? id : null;
};

/**
 * Estado para el badge de la card compartida. UF y lote CAD: siempre (vacío →
 * disponible, igual que la meta). Lote con publicación: solo si el payload trae
 * `stock_status`. El lote solo catastro no tiene este estado (su «Estado» es
 * `lot_state` y sigue en la línea de datos).
 * @param {object|null} item
 * @returns {string|null}
 */
export function resolveShareCardStockStatus(item) {
  if (!item) return null;
  if (item.unit || item.itemType === 'unit') {
    return knownStockStatus(item.unit?.stock_status ?? item.stock_status) || 'available';
  }
  if (item.feature) {
    return knownStockStatus(item.feature?.properties?.stock_status ?? item.stock_status) || 'available';
  }
  return knownStockStatus(item.lot?.lot_user_content?.stock_status);
}

/**
 * Valor de una meta en la línea compacta de la card (sin label). El estado no
 * entra: va en el badge. Números sueltos llevan unidad («2 dorm.»).
 * @param {{ key?: string, value?: unknown }} meta
 * @returns {string|null}
 */
export function formatShareCardMetaValue(meta) {
  if (!meta || meta.key === 'stock' || meta.value == null || (typeof meta.value !== 'string' && typeof meta.value !== 'number')) {
    return null;
  }
  const text = String(meta.value).trim();
  if (!text) return null;
  const n = Number(text);
  switch (meta.key) {
    case 'bedrooms':
      return Number.isFinite(n) ? `${n} dorm.` : text;
    case 'full_bathrooms':
      return Number.isFinite(n) ? (n === 1 ? '1 baño' : `${n} baños`) : text;
    case 'parking_lots':
      return Number.isFinite(n) ? (n === 1 ? '1 cochera' : `${n} cocheras`) : text;
    case 'official_unit':
      return /^uf\b/i.test(text) ? text : `UF ${text}`;
    case 'block':
      return /^mz\b/i.test(text) ? text : `Mz. ${text}`;
    case 'open_surface':
      return `${text} semicub.`;
    case 'balcony':
      return text === 'Sí' ? 'Balcón' : `Balcón ${text}`;
    default:
      return text;
  }
}

/**
 * Totales de la lista compartida: unidades (UF) y cuántas están disponibles.
 * Un lote sin UF no entra: no es una unidad. El conteo sigue el listado filtrado.
 * @param {object[]} groupedLots
 * @returns {{ units: number, available: number }}
 */
export function summarizeSharedListCounts(groupedLots) {
  let units = 0;
  let available = 0;
  (Array.isArray(groupedLots) ? groupedLots : []).forEach((lot) => {
    getDisplayUnits(lot).forEach((unit) => {
      units += 1;
      if (resolveUnitStockStatus(unit) === 'available') available += 1;
    });
  });
  return { units, available };
}

/**
 * «11 unidades · 8 disponibles». Singular en 1.
 * @param {{ units?: number, available?: number }} counts
 */
export function formatSharedListCountsLabel({ units = 0, available = 0 } = {}) {
  const unitsN = Number(units) || 0;
  const availableN = Number(available) || 0;
  const unitsPart = unitsN === 1 ? '1 unidad' : `${unitsN} unidades`;
  const availablePart = availableN === 1 ? '1 disponible' : `${availableN} disponibles`;
  return `${unitsPart} · ${availablePart}`;
}

/** @deprecated Prefer buildLotContentListingMetas / buildUnitListingMetas. */
export const buildListingMetas = (unit, lot) => (
  unit ? buildUnitListingMetas(unit, lot) : buildLotContentListingMetas(lot)
);

export const resolveLotContentTitle = (content) => {
  if (!content) return null;
  return firstNonEmpty(content?.title);
};

export const resolveLotContentHero = (content) => {
  if (!content || !hasLotUserContentCommercialData(content)) return null;
  const priceLabel = formatLotUserContentPrice(content);
  if (!priceLabel) return null;
  const operation = firstNonEmpty(content?.operation);
  return {
    kind: 'price',
    amount: priceLabel,
    caption: (operation && LOT_CONTENT_OPERATION_LABEL[operation]) || 'Precio',
    source: 'lot_content',
  };
};

export const buildLotUserContentPresentation = (content, lotContext = null) => {
  if (!content) return null;
  const description = firstNonEmpty(content?.description, content?.notes);
  return {
    title: resolveLotContentTitle(content),
    description: description ? String(description) : null,
    hero: resolveLotContentHero(content),
    metas: lotContext ? buildLotContentListingMetas(lotContext) : [],
    images: normalizeListingImages(content.images, content.cover_image ?? null),
    videos: normalizeListingVideos(content.videos),
    attachments: normalizeListingAttachments(content.attachments),
  };
};

export const resolveUnitHero = (unit) => {
  const priceLabel = formatListingPrice(unit?.price, unit?.currency);
  if (!priceLabel) return null;
  const operation = resolveUnitOperation(unit);
  return {
    kind: 'price',
    amount: priceLabel,
    caption: (operation && OPERATION_LABEL[operation]) || 'Precio',
    source: 'unit',
  };
};

/** Presentación marketplace para detalle de unidad funcional en vistas compartidas. */
export const buildUnitPresentation = (unit, lotContext = null) => {
  if (!unit) return null;
  const description = firstNonEmpty(unit?.description, unit?.observations);
  return {
    title: firstNonEmpty(unit?.title),
    description: description ? String(description) : null,
    hero: resolveUnitHero(unit),
    metas: buildUnitListingMetas(unit, lotContext),
    images: normalizeListingImages(unit?.images, unit?.cover_image ?? null),
    videos: normalizeListingVideos(unit?.videos),
    attachments: normalizeListingAttachments(unit?.attachments),
  };
};
